/**
 * kv-celebrate
 *
 * Fires a short confetti burst + message flash when the shopper crosses into a
 * higher bundle tier in the cart drawer.
 *
 * Why a custom element and not an inline script: the drawer re-renders through
 * the Section Rendering API, and morphs never re-execute <script> tags. This
 * module is therefore loaded ONCE outside the drawer's morphed subtree (from
 * snippets/bmp-cart-announcements.liquid, script_only branch), and the element
 * below reacts to whatever markup the morph brings in.
 *
 * Why the confetti mounts on <body> and not inside the bar: a cart update can
 * morph the drawer subtree MORE THAN ONCE (optimistic render, then the
 * confirmed one). Anything appended inside .bmp-free-shipping-bar is wiped by
 * the second pass a few hundred ms after the first, which reads as "the
 * confetti never fired". Body-mounted + position:fixed over the bar's rect is
 * immune to that, at the cost of needing to re-measure on each burst.
 *
 * Morphing is handled two ways because either can happen:
 *   - the element is replaced wholesale  -> connectedCallback fires
 *   - the element is reused, attrs patched -> attributeChangedCallback fires
 * Both funnel into sync(), which is idempotent: it only celebrates when the
 * tier is strictly higher than the last one seen this session.
 *
 * Baseline: on the first sync of a session nothing fires. Otherwise every
 * shopper who already had 3 tees in their cart would get confetti on page load.
 */

const STORAGE_KEY = 'kv-bundle-tier';
const PIECES = 30;
const COLORS = ['#c8102e', '#e8a33d', '#3f7d5c', '#4a6b8a', '#e6ddcc'];
const BURST_MS = 1800;
const FLASH_MS = 2600;

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readBaseline = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw === null ? null : Number.parseInt(raw, 10);
  } catch {
    return null;
  }
};

const writeBaseline = (tier) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(tier));
  } catch {
    /* private mode - celebrations just won't dedupe across reloads */
  }
};

/** Only one burst on screen at a time, regardless of how many elements sync. */
let activeBurst = null;

class KvCelebrate extends HTMLElement {
  static observedAttributes = ['tier'];

  connectedCallback() {
    this.sync();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'tier' && oldValue !== newValue && this.isConnected) {
      this.sync();
    }
  }

  sync() {
    const tier = Number.parseInt(this.getAttribute('tier') || '0', 10);
    if (Number.isNaN(tier)) return;

    const baseline = readBaseline();
    writeBaseline(tier);

    if (baseline === null || tier <= baseline) return;

    // Wait a frame: on an optimistic-then-confirmed morph this element may be
    // replaced immediately, and measuring a detached bar gives a zero rect.
    requestAnimationFrame(() => this.celebrate());
  }

  celebrate() {
    const bar =
      this.closest('.bmp-free-shipping-bar') ||
      document.querySelector('.bmp-free-shipping-bar');
    if (!bar) return;

    this.flashMessage(bar);
    if (!prefersReducedMotion()) this.burst(bar);
  }

  flashMessage(bar) {
    const message = this.getAttribute('message');
    const target = bar.querySelector('[data-kv-message]');
    if (!message || !target) return;

    target.textContent = message;
    bar.classList.add('is-celebrating');

    window.clearTimeout(KvCelebrate._flashTimer);
    KvCelebrate._flashTimer = window.setTimeout(() => {
      document
        .querySelectorAll('.bmp-free-shipping-bar.is-celebrating')
        .forEach((el) => el.classList.remove('is-celebrating'));
    }, FLASH_MS);
  }

  burst(bar) {
    const rect = bar.getBoundingClientRect();
    if (rect.width === 0) return;

    if (activeBurst) activeBurst.remove();

    const layer = document.createElement('div');
    layer.className = 'kv-confetti';
    layer.setAttribute('aria-hidden', 'true');
    layer.style.left = `${rect.left}px`;
    layer.style.top = `${rect.top}px`;
    layer.style.width = `${rect.width}px`;
    layer.style.height = `${rect.height}px`;

    for (let i = 0; i < PIECES; i += 1) {
      const piece = document.createElement('span');
      piece.className = 'kv-confetti__piece';
      piece.style.setProperty('--kv-x', `${Math.random() * 100}%`);
      piece.style.setProperty('--kv-drift', `${(Math.random() - 0.5) * 70}px`);
      piece.style.setProperty('--kv-delay', `${Math.random() * 240}ms`);
      piece.style.setProperty('--kv-spin', `${Math.random() * 720 - 360}deg`);
      piece.style.setProperty('--kv-color', COLORS[i % COLORS.length]);
      layer.appendChild(piece);
    }

    document.body.appendChild(layer);
    activeBurst = layer;

    window.setTimeout(() => {
      layer.remove();
      if (activeBurst === layer) activeBurst = null;
    }, BURST_MS);
  }
}

if (!customElements.get('kv-celebrate')) {
  customElements.define('kv-celebrate', KvCelebrate);
}
